<script lang="ts">
  import type { Snippet } from 'svelte';
  // Right-side overlay panel used for drill-downs. Closes on Escape or backdrop
  // click, moves focus in on open, and is full-width on mobile.
  let {
    open,
    onclose,
    label = 'Details',
    scroll = true,
    children,
  }: {
    open: boolean;
    onclose: () => void;
    label?: string;
    /**
     * Whether the PANEL scrolls, or the content does.
     *
     * True keeps every existing caller exactly as it was: one long scroll, header and
     * all. False hands the job down — the child becomes a flex column owning its own
     * scroll region, which is the only way anything inside can be pinned. A `sticky`
     * element sticks inside the thing that SCROLLS, so while this element is the
     * scroller nothing within it can hold still.
     */
    scroll?: boolean;
    children: Snippet;
  } = $props();

  let panel = $state<HTMLDivElement | null>(null);

  $effect(() => {
    if (!open) return;
    function onkey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onclose();
    }
    window.addEventListener('keydown', onkey);
    // Move focus into the panel for keyboard users.
    panel?.focus();
    return () => window.removeEventListener('keydown', onkey);
  });
</script>

{#if open}
  <!-- Backdrop: click-to-close is a mouse convenience; keyboard users close with
       Escape (handled on window above), so no key handler is needed here. -->
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div
    class="fixed inset-0 z-30 flex justify-end bg-black/40 backdrop-blur-[1px]"
    onclick={onclose}
    role="presentation"
  >
    <div
      bind:this={panel}
      class="flex h-full w-full flex-col border-l hair bg-base-100 shadow-2xl outline-none sm:max-w-xl
             {scroll ? 'overflow-y-auto' : 'overflow-hidden'}"
      onclick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabindex="-1"
    >
      {@render children()}
    </div>
  </div>
{/if}
